package com.dotdrives.web.pages.forms

import com.dotdrives.design.DotTone
import com.dotdrives.model.FormOrgStats
import com.dotdrives.model.FormStats
import com.dotdrives.model.GivingForm
import com.dotdrives.model.GivingFormStatus
import com.dotdrives.model.GivingFormType
import com.dotdrives.web.pages.drives.formatMoneyFull

/**
 * `stats == null` means zero `form_submissions` rows were visible for the whole set of forms --
 * an ambiguous RLS-empty result (docs/schema.md), rendered as "-" everywhere. A non-null map
 * with [formId] absent is a trustworthy zero: some row elsewhere proved the caller's role can
 * read this table at all, so an absent form really has no completed submissions.
 */
fun formRaisedCell(stats: Map<String, FormStats>?, formId: String): String =
    if (stats == null) MONEY_HIDDEN else formatMoneyFull(stats[formId]?.raisedAmount ?: 0.0)

fun formGiftsCell(stats: Map<String, FormStats>?, formId: String): String =
    if (stats == null) MONEY_HIDDEN else (stats[formId]?.giftsCount ?: 0).toString()

fun formRecurringCell(stats: Map<String, FormStats>?, formId: String): String =
    if (stats == null) MONEY_HIDDEN else (stats[formId]?.recurringCount ?: 0).toString()

fun formStatusLabel(status: String): String = when (status) {
    GivingFormStatus.LIVE -> "● Live"
    GivingFormStatus.DRAFT -> "Draft"
    GivingFormStatus.ARCHIVED -> "Archived"
    else -> status.replaceFirstChar(Char::uppercase)
}

fun formStatusTone(status: String): DotTone = when (status) {
    GivingFormStatus.LIVE -> DotTone.Success
    GivingFormStatus.DRAFT -> DotTone.Info
    else -> DotTone.Neutral
}

data class ActiveFormsBreakdown(val liveCount: Int, val subtitle: String)

/** "ACTIVE FORMS" stat tile: counts and a "N campaigns, M evergreen" breakdown -- live forms only. */
fun activeFormsBreakdown(forms: List<GivingForm>): ActiveFormsBreakdown {
    val live = forms.filter { it.status == GivingFormStatus.LIVE }
    val counts = live.groupingBy(GivingForm::type).eachCount()
    val subtitle = listOf(GivingFormType.CAMPAIGN, GivingFormType.EVERGREEN, GivingFormType.EVENT)
        .mapNotNull { type ->
            val count = counts[type] ?: 0
            if (count == 0) null else "$count ${typeNoun(type, count)}"
        }
        .joinToString(", ")
    return ActiveFormsBreakdown(liveCount = live.size, subtitle = subtitle)
}

private fun typeNoun(type: String, count: Int): String = when (type) {
    GivingFormType.CAMPAIGN -> if (count == 1) "campaign" else "campaigns"
    GivingFormType.EVERGREEN -> "evergreen"
    GivingFormType.EVENT -> if (count == 1) "event" else "events"
    else -> type
}

fun orgRaisedCell(orgStats: FormOrgStats?): String =
    if (orgStats == null) MONEY_HIDDEN else formatMoneyFull(orgStats.raisedLast30Days)

fun orgGiftsSubtitle(orgStats: FormOrgStats?): String {
    if (orgStats == null) return MONEY_HIDDEN
    val count = orgStats.giftsLast30Days
    return "$count ${if (count == 1) "gift" else "gifts"}"
}

fun formTypeEmoji(type: String): String = when (type) {
    GivingFormType.CAMPAIGN -> "🌊"
    GivingFormType.EVERGREEN -> "🌱"
    GivingFormType.EVENT -> "🎪"
    else -> "📝"
}

fun formTypeLabel(type: String): String = when (type) {
    GivingFormType.CAMPAIGN -> "Campaign"
    GivingFormType.EVERGREEN -> "Evergreen giving page"
    GivingFormType.EVENT -> "Event / registration"
    else -> type.replaceFirstChar(Char::uppercase)
}

fun formTypeDescription(type: String): String = when (type) {
    GivingFormType.CAMPAIGN -> "A goal, an end date, a progress bar — built for momentum"
    GivingFormType.EVERGREEN -> "Always-on general giving, no end date"
    GivingFormType.EVENT -> "Ticketed giving with attendee names"
    else -> ""
}

/** Stable public route on the current web deployment. */
fun publicFormUrl(orgId: String, publicSlug: String): String = "#/give/$orgId/$publicSlug"

/** Submission-feed timestamp, matching design/portal-forms.dc.html's "JUST NOW"/"2 MIN"/"1 HR" style. */
fun relativeTimeLabel(occurredAt: kotlin.time.Instant, now: kotlin.time.Instant): String {
    val minutes = (now - occurredAt).inWholeMinutes
    return when {
        minutes < 1 -> "JUST NOW"
        minutes < 60 -> "$minutes MIN"
        minutes < 60 * 24 -> "${minutes / 60} HR"
        else -> "${minutes / (60 * 24)}D"
    }
}

private val MONTH_NAMES = listOf(
    "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
)

/** "Close form on end date" summary in the Settings tab -- "Dec 24, 2026". */
fun formatCloseDate(instant: kotlin.time.Instant): String {
    val date = kotlin.js.Date(instant.toEpochMilliseconds().toDouble())
    val month = MONTH_NAMES[date.getUTCMonth()]
    return "$month ${date.getUTCDate()}, ${date.getUTCFullYear()}"
}

private const val MONEY_HIDDEN = "—"
